/**
 * 消消看游戏组件
 * 10x10棋盘，点击消除相连的同色宝石
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Position } from '../types/game';
import GameShareButton from './GameShareButton';
import {
  Match3GameState,
  Gem,
  Star,
  Animal,
  Fruit,
  Match3Piece,
  Match3Mode,
  createInitialBoard,
  findAllMatches,
  removeGems,
  dropGems,
  shiftGemsLeft,
  fillEmptySpaces,
  calculateScore,
  hasValidMoves,
  hasAvailableMoves,
  findHappyMatches,
  hasHappyMoves,
  canCreateHappyMatch,
  getTargetScore
} from '../utils/match3';
import Match3Board from './Match3Board';

interface Match3GameProps {
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
  mode?: Match3Mode;
}

const Match3Game: React.FC<Match3GameProps> = ({ onBackToMenu, onBackToModeSelect, mode = 'classic' }) => {
  const [gameState, setGameState] = useState<Match3GameState>(() => ({
    board: createInitialBoard(10, mode),
    score: 0,
    level: 1,
    moves: 0,
    targetScore: getTargetScore(1),
    isAnimating: false,
    selectedGem: null,
    matchedGems: [],
    lastCombo: 0,
    mode,
    baseTargetScore: mode === 'stars' ? 1000 : 0
  }));

  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [showNoMovesHint, setShowNoMovesHint] = useState(false);
  const [levelCompleteManuallyClosed, setLevelCompleteManuallyClosed] = useState(false);
  const [gameOverManuallyClosed, setGameOverManuallyClosed] = useState(false);
  const [showRules, setShowRules] = useState(false);

  // 处理宝石点击
  const handleGemClick = useCallback(async (position: Position) => {
    if (gameState.isAnimating) return;

    // 开心消消乐和水果消消乐模式：选择和交换宝石
    if (gameState.mode === 'happy' || gameState.mode === 'fruit') {
      // 如果还没有选中的宝石，选中当前宝石
      if (!gameState.selectedGem) {
        setGameState(prev => ({ ...prev, selectedGem: position }));
        return;
      }

      // 如果点击的是同一个宝石，取消选中
      if (gameState.selectedGem.row === position.row && gameState.selectedGem.col === position.col) {
        setGameState(prev => ({ ...prev, selectedGem: null }));
        return;
      }

      // 检查是否是相邻的宝石
      const isAdjacent =
        (Math.abs(gameState.selectedGem.row - position.row) === 1 && gameState.selectedGem.col === position.col) ||
        (Math.abs(gameState.selectedGem.col - position.col) === 1 && gameState.selectedGem.row === position.row);

      if (isAdjacent) {
        // 交换宝石
        setGameState(prev => ({ ...prev, isAnimating: true }));

        const newBoard = gameState.board.map(row => [...row]);
        const temp = newBoard[gameState.selectedGem.row][gameState.selectedGem.col];
        newBoard[gameState.selectedGem.row][gameState.selectedGem.col] = newBoard[position.row][position.col];
        newBoard[position.row][position.col] = temp;

        // 检查交换后是否形成三连珠
        const happyMatches = findHappyMatches(newBoard);

        if (happyMatches.length > 0) {
          // 有三连珠，执行消除
          setGameState(prev => ({ ...prev, board: newBoard, selectedGem: null, matchedGems: happyMatches }));

          // 等待动画
          await new Promise(resolve => setTimeout(resolve, 500));

          // 消除宝石
          let boardAfterRemove = removeGems(newBoard, happyMatches);

          // 计算得分
          const points = calculateScore(happyMatches.length, gameState.lastCombo);

          // 下落
          await new Promise(resolve => setTimeout(resolve, 300));
          boardAfterRemove = dropGems(boardAfterRemove);

          // 填充空位
          await new Promise(resolve => setTimeout(resolve, 300));
          boardAfterRemove = fillEmptySpaces(boardAfterRemove, gameState.mode);

          // 检查新的匹配
          let combo = 1;
          let totalPoints = points;

          let newMatches = findHappyMatches(boardAfterRemove);
          while (newMatches.length > 0) {
            combo++;
            totalPoints += calculateScore(newMatches.length, combo);

            setGameState(prev => ({
              ...prev,
              board: boardAfterRemove,
              matchedGems: newMatches,
              lastCombo: combo
            }));

            await new Promise(resolve => setTimeout(resolve, 500));
            boardAfterRemove = removeGems(boardAfterRemove, newMatches);
            await new Promise(resolve => setTimeout(resolve, 300));
            boardAfterRemove = dropGems(boardAfterRemove);
            await new Promise(resolve => setTimeout(resolve, 300));
            boardAfterRemove = fillEmptySpaces(boardAfterRemove, gameState.mode);

            newMatches = findHappyMatches(boardAfterRemove);
          }

          // 更新游戏状态
          const newScore = gameState.score + totalPoints;
          const newMoves = gameState.moves + 1;

          setGameState(prev => ({
            ...prev,
            board: boardAfterRemove,
            score: newScore,
            moves: newMoves,
            matchedGems: [],
            lastCombo: 0,
            isAnimating: false
          }));

          // 检查是否完成关卡
          if (newScore >= gameState.targetScore && !levelCompleteManuallyClosed) {
            setShowLevelComplete(true);
          }

          // 检查游戏是否结束
          if (!hasHappyMoves(boardAfterRemove) && !gameOverManuallyClosed) {
            setShowGameOver(true);
          }
        } else {
          // 没有三连珠，交换回去
          setGameState(prev => ({ ...prev, board: newBoard }));

          await new Promise(resolve => setTimeout(resolve, 300));

          // 交换回来
          const revertBoard = newBoard.map(row => [...row]);
          const temp = revertBoard[gameState.selectedGem.row][gameState.selectedGem.col];
          revertBoard[gameState.selectedGem.row][gameState.selectedGem.col] = revertBoard[position.row][position.col];
          revertBoard[position.row][position.col] = temp;

          setGameState(prev => ({
            ...prev,
            board: revertBoard,
            selectedGem: null,
            isAnimating: false
          }));

          // 检查是否还有其他可能的交换可以形成三连珠
          if (!canCreateHappyMatch(revertBoard)) {
            // 真正没有可能的交换了，游戏结束
            if (!gameOverManuallyClosed) {
              setShowGameOver(true);
            }
          } else {
            // 还有其他可能的交换，只是这次交换无效
            setShowNoMovesHint(true);
            setTimeout(() => {
              setShowNoMovesHint(false);
            }, 1500);
          }
        }
      } else {
        // 不是相邻的宝石，选择新的宝石
        setGameState(prev => ({ ...prev, selectedGem: position }));
      }
      return;
    }

    // 其他模式：传统点击消除
    const piece = gameState.board[position.row][position.col];
    if (!piece) return;

    // 查找与点击宝石相连的同色宝石
    const connectedGems = findConnectedGems(gameState.board, position);

    const minRequired = gameState.mode === 'stars' ? 2 : 3;
    if (connectedGems.length >= minRequired) {
      setGameState(prev => ({ ...prev, isAnimating: true }));

      // 显示匹配的宝石
      setGameState(prev => ({ ...prev, matchedGems: connectedGems }));

      // 等待动画
      await new Promise(resolve => setTimeout(resolve, 500));

      // 消除宝石
      let newBoard = removeGems(gameState.board, connectedGems);

      // 计算得分
      const points = calculateScore(connectedGems.length, gameState.lastCombo);

      // 下落
      await new Promise(resolve => setTimeout(resolve, 300));
      newBoard = dropGems(newBoard);

      // 星星模式：向左移动
      if (gameState.mode === 'stars') {
        await new Promise(resolve => setTimeout(resolve, 200));
        newBoard = shiftGemsLeft(newBoard);
      }

      // 填充空位
      await new Promise(resolve => setTimeout(resolve, 300));
      newBoard = fillEmptySpaces(newBoard, gameState.mode);

      // 检查新的匹配
      let combo = 1;
      let totalPoints = points;

      // 星星模式不进行连锁消除，避免过度消除
      if (gameState.mode !== 'stars') {
        let newMatches = findAllMatches(newBoard);
        while (newMatches.length > 0) {
          combo++;
          totalPoints += calculateScore(newMatches.length, combo);

          setGameState(prev => ({
            ...prev,
            board: newBoard,
            matchedGems: newMatches,
            lastCombo: combo
          }));

          await new Promise(resolve => setTimeout(resolve, 500));
          newBoard = removeGems(newBoard, newMatches);
          await new Promise(resolve => setTimeout(resolve, 300));
          newBoard = dropGems(newBoard);
          await new Promise(resolve => setTimeout(resolve, 300));
          newBoard = fillEmptySpaces(newBoard, gameState.mode);

          newMatches = findAllMatches(newBoard);
        }
      }

      // 更新游戏状态
      const newScore = gameState.score + totalPoints;
      const newMoves = gameState.moves + 1;

      setGameState(prev => ({
        ...prev,
        board: newBoard,
        score: newScore,
        moves: newMoves,
        matchedGems: [],
        lastCombo: 0,
        isAnimating: false
      }));

      // 检查是否完成关卡
      if (newScore >= gameState.targetScore && !levelCompleteManuallyClosed) {
        setShowLevelComplete(true);
      }

      // 检查游戏是否结束
      if (!hasValidMoves(newBoard, gameState.mode)) {
        // 星星模式特殊处理：检查分数是否达到1000
        if (gameState.mode === 'stars') {
          if (newScore >= 1000 && !levelCompleteManuallyClosed) {
            setShowLevelComplete(true);
          } else if (!gameOverManuallyClosed) {
            setShowGameOver(true);
          }
        } else if (!gameOverManuallyClosed) {
          setShowGameOver(true);
        }
      }
    } else {
      // 点击了无法消除的棋子，检查是否完全没有可消除的组合
      let hasMoves = false;
      if (gameState.mode === 'happy' || gameState.mode === 'fruit') {
        hasMoves = hasHappyMoves(gameState.board);
      } else {
        hasMoves = hasAvailableMoves(gameState.board, gameState.mode);
      }

      if (!hasMoves) {
        // 没有可消除的棋子，显示提示
        setShowNoMovesHint(true);
        // 3秒后自动隐藏提示
        setTimeout(() => {
          setShowNoMovesHint(false);
        }, 3000);
      }
    }
  }, [gameState, levelCompleteManuallyClosed, gameOverManuallyClosed]);

  // 获取棋子的标识符（颜色或类型）
  const getPieceIdentifier = (piece: Match3Piece): string => {
    if ((piece as Gem | Star | Animal).color) {
      return (piece as Gem | Star | Animal).color;
    }
    return (piece as Fruit).type;
  };

  // 查找相连的同色宝石
  const findConnectedGems = (board: (Match3Piece | null)[][], startPos: Position): Position[] => {
    const piece = board[startPos.row][startPos.col];
    if (!piece) return [];

    const pieceIdentifier = getPieceIdentifier(piece);

    const visited = new Set<string>();
    const connected: Position[] = [];
    const stack: Position[] = [startPos];

    while (stack.length > 0) {
      const pos = stack.pop()!;
      const key = `${pos.row},${pos.col}`;

      if (visited.has(key)) continue;
      visited.add(key);

      const currentPiece = board[pos.row][pos.col];
      if (!currentPiece) continue;

      // 比较标识符而不是颜色
      const currentIdentifier = getPieceIdentifier(currentPiece);
      if (currentIdentifier !== pieceIdentifier) continue;

      connected.push(pos);

      // 检查相邻的四个方向
      const directions = [
        { row: -1, col: 0 }, // 上
        { row: 1, col: 0 },  // 下
        { row: 0, col: -1 }, // 左
        { row: 0, col: 1 }   // 右
      ];

      for (const dir of directions) {
        const newPos = { row: pos.row + dir.row, col: pos.col + dir.col };
        if (
          newPos.row >= 0 && newPos.row < 10 &&
          newPos.col >= 0 && newPos.col < 10 &&
          !visited.has(`${newPos.row},${newPos.col}`)
        ) {
          stack.push(newPos);
        }
      }
    }

    return connected;
  };

  // 进入下一关
  const handleNextLevel = () => {
    const newLevel = gameState.level + 1;
    setGameState({
      board: createInitialBoard(10, gameState.mode),
      score: gameState.score,
      level: newLevel,
      moves: 0,
      targetScore: getTargetScore(newLevel),
      isAnimating: false,
      selectedGem: null,
      matchedGems: [],
      lastCombo: 0,
      mode: gameState.mode,
      baseTargetScore: gameState.baseTargetScore
    });
    setShowLevelComplete(false);
    setLevelCompleteManuallyClosed(false);
    setShowNoMovesHint(false);
  };

  // 重新开始游戏
  const handleRestart = () => {
    setGameState({
      board: createInitialBoard(10, gameState.mode),
      score: 0,
      level: 1,
      moves: 0,
      targetScore: getTargetScore(1),
      isAnimating: false,
      selectedGem: null,
      matchedGems: [],
      lastCombo: 0,
      mode: gameState.mode,
      baseTargetScore: gameState.baseTargetScore
    });
    setShowGameOver(false);
    setGameOverManuallyClosed(false);
    setShowLevelComplete(false);
    setLevelCompleteManuallyClosed(false);
    setShowNoMovesHint(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">💎</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent sm:mb-1">
                {gameState.mode === 'classic' ? '经典宝石消除' :
                 gameState.mode === 'stars' ? '星星消除挑战' :
                 gameState.mode === 'happy' ? '开心消消乐' :
                 gameState.mode === 'fruit' ? '水果消消乐' : '消消看'}
              </h1>
              <span className="text-2xl">⭐</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                第 {gameState.level} 关
              </span>

              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-orange-500 to-red-500 text-white">
                🎯 目标: {gameState.targetScore} 分
              </span>
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button onClick={onBackToMenu} className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium" title="回到首页">
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}
            {/* 返回按钮 */}
            {onBackToModeSelect && (
              <button onClick={onBackToModeSelect} className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium" title="返回模式选择">
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button onClick={handleRestart} className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium" title="重新开始">
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 - 暂时禁用，因为消消看游戏通常不支持悔棋 */}
            <button disabled className="flex items-center space-x-1 px-2 py-1.5 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed text-xs font-medium" title="暂不支持悔棋">
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button onClick={() => setShowRules(true)} className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium" title="游戏规则">
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="match3" match3Mode={gameState.mode} />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-between">
          {/* 当前得分 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
            <span className="text-sm sm:text-base font-medium">
              得分: {gameState.score}
            </span>
          </div>

          {/* 其他信息 */}
          <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
            <span>步数: {gameState.moves}</span>
            {gameState.lastCombo > 1 && (
              <span className="text-purple-600 font-bold">
                连击 x{gameState.lastCombo}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 进度条 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center space-x-3">
          <span className="text-xs text-gray-600">进度:</span>
          <div className="flex-1 bg-gray-200 rounded-full h-4 relative overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-500"
              style={{ width: `${Math.min((gameState.score / gameState.targetScore) * 100, 100)}%` }}
            />
          </div>
          <span className="text-xs text-gray-600">
            {Math.round((gameState.score / gameState.targetScore) * 100)}%
          </span>
        </div>
      </div>

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full max-w-none">
          <Match3Board
            board={gameState.board}
            onGemClick={handleGemClick}
            selectedGem={gameState.selectedGem}
            matchedGems={gameState.matchedGems}
            disabled={gameState.isAnimating}
            mode={gameState.mode}
          />
        </div>
      </div>

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-purple-50 border-t border-purple-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-purple-800 text-center leading-relaxed">
          <strong>规则：</strong>
          {gameState.mode === 'happy' ?
            '选择小动物后再点击相邻小动物交换位置 | 交换后形成三连珠即可消除 | 消除后自动填充新小动物 | 达到目标分数进入下一关' :
            gameState.mode === 'fruit' ?
            '选择水果后再点击相邻水果交换位置 | 交换后形成三连珠即可消除 | 消除后自动填充新水果 | 达到目标分数进入下一关' :
            gameState.mode === 'stars' ?
            '点击相连的同色星星消除 | 2个以上才可消除 | 消除越多得分越高 | 消除空列时星星会向左移动 | 清空所有星星获得高分' :
            '点击相连的同色宝石消除 | 3个以上才可消除 | 消除越多得分越高 | 达到目标分数进入下一关'
          }
        </p>
      </div>

      {/* 关卡完成对话框 */}
      {showLevelComplete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl relative">
            {/* 关闭按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowLevelComplete(false);
                setLevelCompleteManuallyClosed(true);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4">
              <span className="text-green-600">
                🎉 {gameState.mode === 'stars' ? '挑战成功！' : '关卡完成！'}
              </span>
            </h2>

            <div className="text-center text-gray-600 mb-6">
              {gameState.mode === 'stars' ? (
                <>
                  <p>恭喜达到1000分目标！</p>
                  <p>当前得分: {gameState.score}</p>
                  <p>使用步数: {gameState.moves}</p>
                  <p className="text-green-600 font-medium">可以进入下一关继续挑战！</p>
                </>
              ) : (
                <>
                  <p>恭喜完成第 {gameState.level} 关！</p>
                  <p>本关得分: {gameState.score}</p>
                  <p>使用步数: {gameState.moves}</p>
                </>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={handleNextLevel}
                className="w-full bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                下一关
              </button>
              <button
                onClick={handleRestart}
                className="w-full bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                重新开始
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 游戏结束对话框 */}
      {showGameOver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl relative">
            {/* 关闭按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowGameOver(false);
                setGameOverManuallyClosed(true);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4">
              <span className="text-red-600">
                {gameState.mode === 'stars' ? '挑战失败' :
                 gameState.mode === 'happy' ? '无法继续' : '游戏结束'}
              </span>
            </h2>

            <div className="text-center text-gray-600 mb-6">
              <p>
                {gameState.mode === 'happy'
                  ? '棋盘上无法通过任何小动物交换形成三连珠了！'
                  : gameState.mode === 'stars'
                  ? `没有可消除的星星了！${gameState.score < 1000 ? '未达到1000分目标。' : ''}`
                  : '没有可用的移动了！'
                }
              </p>
              <p>最终得分: {gameState.score}</p>
              <p>到达关卡: {gameState.level}</p>
              <p>总步数: {gameState.moves}</p>
              {gameState.mode === 'stars' && gameState.score < 1000 && (
                <p className="text-orange-600 font-medium">距离1000分目标还差 {1000 - gameState.score} 分</p>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={handleRestart}
                className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                重新开始
              </button>
              <button
                onClick={onBackToModeSelect || onBackToMenu}
                className="w-full bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                {onBackToModeSelect ? '返回模式选择' : '返回主页'}
              </button>
              {onBackToMenu && onBackToModeSelect && (
                <button
                  onClick={onBackToMenu}
                  className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
                >
                  返回首页
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4 text-gray-800 pr-8">
              💎 消消看游戏规则
            </h2>

            <div className="text-gray-700 space-y-4 mb-6">
              <div>
                <h3 className="font-bold text-lg mb-2">游戏目标</h3>
                <p>通过消除相同类型的元素来获得分数，达到目标分数即可过关！</p>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">游戏模式</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>经典宝石消除：</strong>点击3个以上相连的同色宝石消除</li>
                  <li><strong>星星消除挑战：</strong>点击2个以上相连的同色星星，达到1000分获胜</li>
                  <li><strong>开心消消乐：</strong>交换相邻小动物，形成三连珠消除</li>
                  <li><strong>水果消消乐：</strong>交换相邻水果，形成三连珠消除</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">操作方式</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>经典/星星模式：</strong>直接点击相连的同色元素进行消除</li>
                  <li><strong>开心/水果模式：</strong>先选择一个元素，再点击相邻元素进行交换</li>
                  <li>交换后如果形成三连珠，则消除并获得分数</li>
                  <li>如果交换后没有形成三连珠，则自动换回原位</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">消除规则</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>经典模式：</strong>需要3个以上相连的同色宝石</li>
                  <li><strong>星星模式：</strong>只需要2个以上相连的同色星星</li>
                  <li><strong>开心/水果模式：</strong>必须是横向或纵向3个连线</li>
                  <li>消除的元素越多，获得的分数越高</li>
                  <li>星星模式特殊：消除空列时，星星会向左移动</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">连击系统</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>消除后上方元素会下落填充空位</li>
                  <li>新元素填充后如果再次形成可消除组合，会自动消除</li>
                  <li>连续消除会形成连击，获得额外加分</li>
                  <li>连击次数越多，分数加成越高</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">获胜条件</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>经典/开心/水果模式：</strong>达到每关的目标分数</li>
                  <li><strong>星星模式：</strong>累计达到1000分即可获胜</li>
                  <li>无法继续消除时游戏结束</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">策略提示</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>优先消除数量较多的组合，获得更高分数</li>
                  <li>创造连击机会，获得额外加分</li>
                  <li>观察棋盘整体，规划最优的消除顺序</li>
                  <li>开心/水果模式：考虑交换后的连锁反应</li>
                  <li>星星模式：合理利用向左移动的特性</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowRules(false)}
              className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {/* 无可消除提示 */}
      {showNoMovesHint && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-yellow-100 border-2 border-yellow-400 text-yellow-800 px-6 py-4 rounded-lg shadow-lg animate-pulse">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">💡</div>
              <div>
                <h3 className="font-bold text-lg">
                  {gameState.mode === 'stars' ? '没有可消除的星星了！' :
                   gameState.mode === 'happy' ? '无效交换！' :
                   '没有可消除的宝石了！'}
                </h3>
                <p className="text-sm">
                  {gameState.mode === 'happy' ? (
                    '这次交换无法形成三连珠，请尝试其他相邻小动物交换'
                  ) : gameState.mode === 'stars' ? (
                    gameState.score >= 1000
                      ? '恭喜！你已经达到1000分目标，可以挑战成功！'
                      : `当前得分：${gameState.score}分，距离1000分目标还差 ${1000 - gameState.score} 分`
                  ) : (
                    gameState.score >= gameState.targetScore
                      ? '恭喜！你已经达到目标分数，可以过关！'
                      : `当前得分：${gameState.score}分，距离目标分数还差 ${gameState.targetScore - gameState.score} 分`
                  )}
                </p>
                <p className="text-xs mt-1">
                  {gameState.mode === 'happy'
                    ? '仔细观察棋盘，寻找能够形成三连珠的小动物交换机会'
                    : gameState.mode === 'stars'
                    ? '游戏即将结束，请做好挑战失败准备'
                    : '游戏即将结束，请做好重新开始准备'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Match3Game;